import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { cn } from "@core/utils";

interface SegmentedOption {
  id: string;
  label: string;
}

interface SegmentedControlProps {
  options: readonly SegmentedOption[];
  value: string;
  onChange: (id: string) => void;
  "aria-label": string;
  isDisabled?: boolean;
  className?: string;
}

/**
 * One choice among a few, as a track with a raised selected segment: « Français / English » on
 * « Mon compte ». Same behaviour as the language switcher of the auth pages (a single-selection
 * toggle group, never empty); this one is generic and takes the options from the caller.
 */
export function SegmentedControl({
  options,
  value,
  onChange,
  isDisabled,
  className,
  ...props
}: SegmentedControlProps) {
  return (
    <ToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      isDisabled={isDisabled}
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const next = [...keys][0];
        if (typeof next === "string" && next !== value) onChange(next);
      }}
      aria-label={props["aria-label"]}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full bg-ink-100 p-0.75",
        className
      )}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.id}
          id={option.id}
          className={cn(
            "cursor-pointer rounded-full px-md py-1.5 outline-none transition-colors",
            "text-sm-medium text-(--text-secondary)",
            "focus-visible:ring-2 focus-visible:ring-focus-ring",
            "disabled:cursor-not-allowed disabled:text-(--text-disabled)",
            "selected:bg-surface-card selected:text-(--text-primary) selected:shadow-xs"
          )}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export type { SegmentedControlProps, SegmentedOption };
